import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { FiRefreshCw, FiSearch } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useDebounce } from '../../../hooks/useDebounce'
import { Pagination } from '../../../utils/pagination.utils'

interface User { id: number; first_name: string; last_name: string }
interface Activity { id: number; activity_name: string }
interface Project { id: number; project_name: string; project_short_name?: string; activity?: Activity }
interface Vendor { id: number; vendor_full_name: string; vendor_code?: string; vendor_owner_user_id?: number; owner?: User | null }

interface InvoiceSite {
  id: number
  site_id?: string
  site_name?: string
  reference_number?: string
  survey_workflow_status?: string
  site_engineer_assign_date?: string | null
  site_engineer_work_date?: string | null
  ta_work_date?: string | null
  report_designer_work_date?: string | null
  validator_work_date?: string | null
  report_complete_date?: string | null
  report_sent_to_client_date?: string | null
  project?: Project
  site_engineer?: User | null
  report_designer?: User | null
  mis?: User | null
  team_coordinator?: User | null
}

interface Invoice {
  id: number
  po_number: string
  po_date: string
  item: string
  item_description: string
  invoice_number?: string
  invoice_date?: string
  working_month?: string | null
  status: string
  invoice_group?: 'field_work' | 'report_prep' | 'complete'
  vendor?: Vendor
  billing_coordinator?: User
  team_coordinator?: User | null
  site?: InvoiceSite
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  po_not_issued: { label: 'PO Not Issued', color: 'bg-gray-100 text-gray-500' },
  po_not_required: { label: 'PO Not Required', color: 'bg-orange-100 text-orange-700' },
  po_generated: { label: 'PO Generated', color: 'bg-blue-100 text-blue-700' },
  invoice_submitted: { label: 'Invoice Submitted', color: 'bg-amber-100 text-amber-700' },
  invoice_booked: { label: 'Invoice Booked', color: 'bg-green-100 text-green-700' },
  invoice_rejected: { label: 'Invoice Rejected', color: 'bg-red-100 text-red-700' },
}

// PO Group Stage — groups the invoice/PO status into PO lifecycle stages.
const PO_GROUP_STAGE_CONFIG: Record<string, { label: string; color: string }> = {
  po_not_issued: { label: 'PO Pending', color: 'bg-gray-100 text-gray-500' },
  po_not_required: { label: 'PO Cancelled', color: 'bg-red-100 text-red-700' },
  po_generated: { label: 'PO Created', color: 'bg-green-100 text-green-700' },
  invoice_submitted: { label: 'PO Created', color: 'bg-green-100 text-green-700' },
  invoice_rejected: { label: 'PO Created', color: 'bg-green-100 text-green-700' },
  invoice_booked: { label: 'PO Created', color: 'bg-green-100 text-green-700' },
}

const INVOICE_STAGE_OPTIONS = [
  { value: '', label: 'All Stages' },
  { value: 'po_generated', label: 'PO Generated' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'invoice_booked', label: 'Invoice Booked' },
  { value: 'invoice_rejected', label: 'Invoice Rejected' },
]

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fullName = (u?: { first_name: string; last_name: string } | null) =>
  u ? `${u.first_name} ${u.last_name}` : '—'
const humanize = (s?: string | null) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[status]?.color ?? 'bg-gray-100 text-gray-600'}`}>
    {STATUS_CONFIG[status]?.label ?? status}
  </span>
)

const POGroupStageBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = PO_GROUP_STAGE_CONFIG[status] || { label: '—', color: 'bg-gray-100 text-gray-500' }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
}

// The 6 columns appended to every table (item 2).
const ADDED_HEADERS = [
  'Vendor PO Number', 'Vendor PO Date', 'Vendor Invoice Number', 'Vendor Invoice Date',
  'Vendor Invoice Stage', 'PO Group Stage',
]

const AddedCells: React.FC<{ inv: Invoice }> = ({ inv }) => (
  <>
    <td className="px-3 py-3 font-mono font-semibold text-blue-700 text-xs whitespace-nowrap">{inv.po_number}</td>
    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(inv.po_date)}</td>
    <td className="px-3 py-3 font-mono text-xs text-gray-900">{inv.invoice_number || '—'}</td>
    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">{fmt(inv.invoice_date)}</td>
    <td className="px-3 py-3"><StatusBadge status={inv.status} /></td>
    <td className="px-3 py-3"><POGroupStageBadge status={inv.status} /></td>
  </>
)

const projectName = (s?: InvoiceSite) => s?.project?.project_short_name ?? s?.project?.project_name ?? '—'
const vendorCode = (inv: Invoice) => inv.vendor?.vendor_code ?? '—'
const vendorOwner = (inv: Invoice) => inv.vendor?.owner ? fullName(inv.vendor.owner) : (inv.vendor?.vendor_full_name ?? '—')
const vendorOwnerId = (inv: Invoice) => String(inv.vendor?.owner?.id ?? inv.vendor?.vendor_owner_user_id ?? '—')

// ── Column headers per table (site-table columns + the 6 added columns) ─────────
const FIELD_HEADERS = [
  'Site ID', 'Site Name', 'Activity', 'Project', 'Engineer Name', 'Vendor Code', 'Vendor Owner', 'Vendor Owner ID',
  'SE Assign Date', 'Field Work Start', 'FW Complete Date', 'Team Coordinator', 'Stage', ...ADDED_HEADERS,
]
const REPORT_HEADERS = [
  'Site ID', 'Site Name', 'Activity', 'Project', 'Report Maker', 'MIS', 'Vendor Code', 'Vendor Owner', 'Vendor Owner ID',
  'Report Assign Date', 'Report Completion Date', 'Report Validation Date', 'Report Sent to Client Date', 'Stage', ...ADDED_HEADERS,
]
const COMPLETE_HEADERS = [
  'Site ID', 'Site Name', 'Activity', 'Project', 'Engineer Name', 'Report Maker', 'MIS', 'Team Coordinator',
  'Vendor Code', 'Vendor Owner', 'Vendor Owner ID',
  'SE Assign Date', 'Field Work Start', 'FW Complete Date',
  'Report Assign Date', 'Report Completion Date', 'Report Validation Date', 'Report Sent to Client Date',
  'Stage', ...ADDED_HEADERS,
]

const FieldRow: React.FC<{ inv: Invoice }> = ({ inv }) => {
  const s = inv.site
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-3 text-gray-500 text-xs font-mono">{s?.site_id ?? '—'}</td>
      <td className="px-3 py-3 font-medium text-gray-900">{s?.site_name ?? '—'}</td>
      <td className="px-3 py-3 text-gray-600">{s?.project?.activity?.activity_name ?? '—'}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{projectName(s)}</td>
      <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(s?.site_engineer)}</td>
      <td className="px-3 py-3 text-gray-500 text-xs font-mono">{vendorCode(inv)}</td>
      <td className="px-3 py-3 text-gray-700">{vendorOwner(inv)}</td>
      <td className="px-3 py-3 text-gray-500 text-xs">{vendorOwnerId(inv)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.site_engineer_assign_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.site_engineer_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.ta_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs">{fullName(s?.team_coordinator)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{humanize(s?.survey_workflow_status)}</td>
      <AddedCells inv={inv} />
    </tr>
  )
}

const ReportRow: React.FC<{ inv: Invoice }> = ({ inv }) => {
  const s = inv.site
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-3 text-gray-500 text-xs font-mono">{s?.site_id ?? '—'}</td>
      <td className="px-3 py-3 font-medium text-gray-900">{s?.site_name ?? '—'}</td>
      <td className="px-3 py-3 text-gray-600">{s?.project?.activity?.activity_name ?? '—'}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{projectName(s)}</td>
      <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(s?.report_designer)}</td>
      <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(s?.mis)}</td>
      <td className="px-3 py-3 text-gray-500 text-xs font-mono">{vendorCode(inv)}</td>
      <td className="px-3 py-3 text-gray-700">{vendorOwner(inv)}</td>
      <td className="px-3 py-3 text-gray-500 text-xs">{vendorOwnerId(inv)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.report_designer_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.report_complete_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.validator_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.report_sent_to_client_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{humanize(s?.survey_workflow_status)}</td>
      <AddedCells inv={inv} />
    </tr>
  )
}

const CompleteRow: React.FC<{ inv: Invoice }> = ({ inv }) => {
  const s = inv.site
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-3 text-gray-500 text-xs font-mono">{s?.site_id ?? '—'}</td>
      <td className="px-3 py-3 font-medium text-gray-900">{s?.site_name ?? '—'}</td>
      <td className="px-3 py-3 text-gray-600">{s?.project?.activity?.activity_name ?? '—'}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{projectName(s)}</td>
      <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(s?.site_engineer)}</td>
      <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(s?.report_designer)}</td>
      <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(s?.mis)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs">{fullName(s?.team_coordinator)}</td>
      <td className="px-3 py-3 text-gray-500 text-xs font-mono">{vendorCode(inv)}</td>
      <td className="px-3 py-3 text-gray-700">{vendorOwner(inv)}</td>
      <td className="px-3 py-3 text-gray-500 text-xs">{vendorOwnerId(inv)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.site_engineer_assign_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.site_engineer_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.ta_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.report_designer_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.report_complete_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.validator_work_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(s?.report_sent_to_client_date)}</td>
      <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{humanize(s?.survey_workflow_status)}</td>
      <AddedCells inv={inv} />
    </tr>
  )
}

interface InvoiceTableProps {
  title: string
  subtitle: string
  headers: string[]
  rows: Invoice[]
  loading: boolean
  RowComponent: React.FC<{ inv: Invoice }>
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ title, subtitle, headers, rows, loading, RowComponent }) => (
  <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/60">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-xs text-gray-500 mt-0.5">{subtitle} — {rows.length} site{rows.length !== 1 ? 's' : ''}</p>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {headers.map(h => (
              <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <tr key={i}>{headers.map((_, j) => <td key={j} className="px-3 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
            ))
          ) : rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-4 py-8 text-center text-gray-400 text-sm">No sites in this group</td></tr>
          ) : rows.map(inv => <RowComponent key={inv.id} inv={inv} />)}
        </tbody>
      </table>
    </div>
  </div>
)

export type InvoiceSiteGroup = 'field_work' | 'report_prep' | 'complete'

// Per-group page configuration — each Invoice Site List sub-menu renders one table.
const GROUP_CONFIG: Record<InvoiceSiteGroup, {
  title: string
  subtitle: string
  headers: string[]
  RowComponent: React.FC<{ inv: Invoice }>
}> = {
  field_work: {
    title: 'Field Work',
    subtitle: 'Sites where you are the Site Engineer only',
    headers: FIELD_HEADERS,
    RowComponent: FieldRow,
  },
  report_prep: {
    title: 'Report Preparation',
    subtitle: 'Sites where you are the Report Maker only',
    headers: REPORT_HEADERS,
    RowComponent: ReportRow,
  },
  complete: {
    title: 'Complete Site Invoices',
    subtitle: 'Sites where you are both the Site Engineer and Report Maker',
    headers: COMPLETE_HEADERS,
    RowComponent: CompleteRow,
  },
}

export default function VendorPortalSiteInvoices({ group }: { group: InvoiceSiteGroup }) {
  const cfg = GROUP_CONFIG[group]
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const debouncedSearch = useDebounce(search, 400)

  const fetchInvoices = useCallback(() => {
    setLoading(true)
    const params: any = { page, limit, group }
    if (statusFilter) params.status = statusFilter
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/invoice-sites', { params })
      .then(r => {
        setInvoices(r.data.data?.rows ?? [])
        setTotal(r.data.data?.count ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [page, limit, statusFilter, debouncedSearch, group])

  useEffect(() => { fetchInvoices() }, [fetchInvoices])
  useEffect(() => { setPage(1) }, [debouncedSearch, group])

  return (
    <>
      <Helmet><title>{cfg.title} – Invoice Site List</title></Helmet>
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{cfg.title}</h2>
              <p className="text-sm text-gray-600 mt-1">{cfg.subtitle} — {total} site{total !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search site / ref..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 w-56"
                />
                <FiSearch className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
              </div>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
                className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500"
              >
                {INVOICE_STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={fetchInvoices} className="p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-600">
                <FiRefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <InvoiceTable
          title={cfg.title}
          subtitle={cfg.subtitle}
          headers={cfg.headers}
          rows={invoices}
          loading={loading}
          RowComponent={cfg.RowComponent}
        />

        <div className="bg-white rounded shadow-sm border border-gray-200">
          <Pagination
            pagination={{
              page,
              limit,
              total_count: total,
              total_pages: Math.ceil(total / limit) || 1,
              has_next_page: page * limit < total,
              has_prev_page: page > 1,
              next_page: page * limit < total ? page + 1 : null,
              prev_page: page > 1 ? page - 1 : null,
              offset: (page - 1) * limit,
            }}
            onPageChange={p => setPage(p)}
            onLimitChange={l => { setLimit(l); setPage(1) }}
          />
        </div>
      </div>
    </>
  )
}
